import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { createExecution, getExecution } from '../../api';
import { useResource } from '../../hooks';
import { FailureInterpretationPanel } from '../../components/FailureInterpretationPanel';
import type { OnboardingPlan } from '../../api';
import { OnboardingPlanCard } from '../../components/OnboardingPlanCard';
import { ExecutionRunView } from '../../components/ExecutionRunView';
import { PanelCard, ProjectSection, QuietEmpty, SectionHeader } from '../../components/project/ui';
import type { SetupProgress } from '../../components/project/projectHealth';
import { Callout, Checklist } from '../../components/primitives';
import { actionLabel } from '../../components/operation/actionLabels';
import { EvidenceActions } from '../../components/operation/OperationSummary';
import { useProject } from './ProjectContext';

/**
 * SetupSection is the guided flow that used to sit permanently on the
 * project page: pick a server, let Commerce Ops read the repository and
 * plan what it would create, reuse or leave alone, approve it, run it.
 *
 * It lives at its own URL so the Overview can stay about the running store.
 * Once the environment is on a server the flow is still here — as the
 * record of how it got there, and the way to plan a change — but nothing
 * pushes anyone into it.
 */
export function SetupSection({ setup }: { setup: SetupProgress }) {
  const project = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const { environment, sectionPath } = project;
  const location = useLocation();
  // The Magento wizard creates the project and asks for the first plan in
  // one go; when planning failed there, the reason arrives here instead of
  // being dropped (CO-73).
  const wizardPlanError = (location.state as { planError?: string } | null)?.planError ?? null;
  const openRunId = searchParams.get('run');

  function openRun(executionId: string) {
    const next = new URLSearchParams(searchParams);
    next.set('run', executionId);
    setSearchParams(next);
  }

  // Starting a plan's run is idempotent on the API: a plan whose run is
  // already in flight answers with that run, so "Run plan" after
  // navigating away reopens it instead of queueing a second one. Errors
  // are thrown to the plan card, which shows them next to the button.
  async function launch(plan: OnboardingPlan) {
    const result = await createExecution(project.projectId, plan.id);
    project.plan.reload();
    openRun(result.execution.id);
  }

  if (!environment) {
    return (
      <ProjectSection>
        <QuietEmpty testId="no-environment">
          No environment to set up · <Link to={sectionPath('settings')}>Add an environment</Link>
        </QuietEmpty>
      </ProjectSection>
    );
  }

  if (openRunId) {
    return (
      <ProjectSection>
        <ExecutionRunView
          executionId={openRunId}
          sectionPath={project.sectionPath}
          onSettled={project.refresh}
          onBack={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('run');
            setSearchParams(next, { replace: true });
            project.refresh();
          }}
        />
      </ProjectSection>
    );
  }

  const todoSteps = setup.steps.filter((step) => step.state === 'todo').map((step) => step.label.toLowerCase()).join(', ');

  return (
    <ProjectSection>
      <LastFailedRun runId={project.environmentHealth.data?.setup.state === 'failed' ? project.environmentHealth.data.setup.run_id : undefined} onOpen={openRun} />
      <PanelCard labelledBy="setup-progress" testId="setup-progress">
        <SectionHeader
          id="setup-progress"
          /* The plan card below carries the workflow's own title; this one
             states where the flow stands, so the two never read as the
             same heading twice. */
          title={setup.complete ? 'Setup complete' : 'Setup progress'}
          actions={
            setup.complete ? (
              <Link className="button button--secondary" to={sectionPath('infrastructure')}>
                View infrastructure
              </Link>
            ) : null
          }
        />
        <Checklist
          label="Setup steps"
          testId="setup-steps"
          items={setup.steps.map((step) => ({
            key: step.key,
            label: step.label,
            state: step.state,
            note: step.state === 'not_needed' ? 'not needed' : undefined,
          }))}
        />
        {/* "Setup complete" already says every step is done; the line stays
            for the cases it adds something to. */}
        {setup.complete && setup.done === setup.total ? null : (
          <p className="panel__note muted" data-testid="setup-count">
            <span aria-hidden="true">
              {setup.complete ? `To do · ${todoSteps}` : `${setup.done}/${setup.total} steps`}
            </span>
            <span className="visually-hidden">{setup.complete ? `Still to do: ${todoSteps}` : `${setup.done} of ${setup.total} steps done`}</span>
          </p>
        )}
      </PanelCard>

      {wizardPlanError && !project.plan.data ? (
        <Callout tone="warning" role="alert" title="The project was created, but the first setup plan could not be made." testId="wizard-plan-error">
          {wizardPlanError} Choose the server below and plan the setup again.
        </Callout>
      ) : null}

      {project.plan.error ? (
        <Callout
          tone="danger"
          title="The setup plan could not be loaded."
          testId="plan-load-error"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => project.plan.reload()}>
              {actionLabel.retry}
            </button>
          }
        >
          {project.plan.error.message}
        </Callout>
      ) : null}

      <OnboardingPlanCard
        isHeadless={project.isHeadless}
        key={`plan-${environment.id}`}
        projectId={project.projectId}
        projectSlug={project.project.slug}
        environment={environment}
        servers={project.servers.data?.servers ?? []}
        currentPlan={project.plan.data}
        onPlanUpdated={project.refresh}
        onLaunchExecution={launch}
        onOpenExecution={openRun}
      />
    </ProjectSection>
  );
}

/**
 * LastFailedRun puts the outcome first (UX-10): when the latest setup run
 * failed, what that means and what to do sit at the top of Setup, not at
 * the bottom of the plan.
 */
function LastFailedRun({ runId, onOpen }: { runId?: string; onOpen: (id: string) => void }) {
  const project = useProject();
  const detail = useResource(() => (runId ? getExecution(runId) : Promise.resolve(null)), [runId], runId);
  if (!runId) return null;
  if (detail.data?.interpretation) {
    return (
      <div className="stack" data-testid="setup-last-failure">
        <FailureInterpretationPanel
          interpretation={detail.data.interpretation}
          sectionPath={project.sectionPath}
          onShowStep={() => onOpen(runId)}
        />
        <div>
          <button type="button" className="button button--secondary" onClick={() => onOpen(runId)}>
            {actionLabel.viewError}
          </button>
        </div>
      </div>
    );
  }
  // The environment says the last run failed. Without an interpretation (the
  // read failed, or the failure could not be classified) that is still said,
  // with the evidence one click away; no cause is invented (rule C).
  if (detail.loading) return null;
  return (
    <Callout
      tone="warning"
      title="The last setup run failed"
      testId="setup-last-failure"
      actions={
        <>
          {detail.error ? (
            <button type="button" className="button button--secondary button--small" onClick={detail.reload}>
              {actionLabel.retry}
            </button>
          ) : null}
          <EvidenceActions onViewError={() => onOpen(runId)} />
        </>
      }
    >
      {detail.error
        ? `Its details could not be loaded: ${detail.error.message}`
        : 'Cause unknown: the failure could not be classified from its output, so no fix is suggested.'}
    </Callout>
  );
}
